/**
 * videoPodRealization.js — Pod Realization layer of the Video Implementation Passport.
 *
 * For CTV / VMAP / ad-pod video, compares the requested pod PLAN (VMAP ad-rules /
 * ORTB podded-impression params) against what the VAST/VMAP response actually
 * DELIVERED: how many ads filled the pod, whether total pod duration stayed within
 * budget, whether ad breaks materialized at all, and whether competitive
 * separation held across adjacent slots.
 *
 * Pure, single-row logic — no React/DOM, no external deps. Never throws: URL
 * parsing is wrapped in try/catch and every check is skipped (not emitted as a
 * false/empty row) when its required inputs are missing.
 *
 * NOTE — scope: `scor` (stream correlator) consistency across ad breaks is a
 * SESSION-level check requiring multiple requests over the life of a stream, so
 * it is intentionally OUT OF SCOPE for this single-row function. That belongs in
 * VideoLifecycleView's session diagnostics, not here.
 */

const VIDEO_TYPES = new Set(['gam_video', 'ima', 'amazon_video', 'springserve_ad_request']);

function parseQueryParams(url) {
  if (!url) return null;
  try {
    return new URL(url).searchParams;
  } catch {
    return null;
  }
}

function toNumberOrNull(val) {
  if (val === null || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}

function sumDeliveredDuration(ads) {
  if (!Array.isArray(ads) || ads.length === 0) return null;
  let sum = 0;
  let any = false;
  for (const ad of ads) {
    const d = ad && ad.linear && ad.linear.durationSeconds;
    if (typeof d === 'number') {
      sum += d;
      any = true;
    }
  }
  return any ? sum : null;
}

function buildPodFillCheck(maxAds, deliveredAds) {
  if (deliveredAds === null) return null;

  if (maxAds !== null) {
    if (deliveredAds < maxAds) {
      return {
        key: 'pod_fill',
        label: 'Pod fill',
        requested: `up to ${maxAds} ads`,
        delivered: `${deliveredAds} ads`,
        state: 'warn',
        note: 'under-filled pod — unsold slots, revenue left on the table',
      };
    }
    if (deliveredAds === maxAds) {
      return {
        key: 'pod_fill',
        label: 'Pod fill',
        requested: `up to ${maxAds} ads`,
        delivered: `${deliveredAds} ads`,
        state: 'pass',
        note: 'full pod',
      };
    }
    return {
      key: 'pod_fill',
      label: 'Pod fill',
      requested: `up to ${maxAds} ads`,
      delivered: `${deliveredAds} ads`,
      state: 'warn',
      note: 'more ads than the requested max',
    };
  }

  return {
    key: 'pod_fill',
    label: 'Pod fill',
    requested: '—',
    delivered: `${deliveredAds} ads`,
    state: 'info',
    note: 'no max-ads plan to compare',
  };
}

function buildPodDurationCheck(podMax, deliveredDur) {
  if (deliveredDur === null) return null;

  if (podMax !== null && podMax > 0) {
    if (deliveredDur > podMax) {
      return {
        key: 'pod_duration',
        label: 'Pod duration',
        requested: `≤ ${podMax}s`,
        delivered: `${deliveredDur}s`,
        state: 'fail',
        note: 'pod exceeds max duration — will overrun the break',
      };
    }
    if (deliveredDur < podMax * 0.5) {
      return {
        key: 'pod_duration',
        label: 'Pod duration',
        requested: `≤ ${podMax}s`,
        delivered: `${deliveredDur}s`,
        state: 'info',
        note: 'pod well under budget — likely under-filled',
      };
    }
    return {
      key: 'pod_duration',
      label: 'Pod duration',
      requested: `≤ ${podMax}s`,
      delivered: `${deliveredDur}s`,
      state: 'pass',
      note: 'within budget',
    };
  }

  return {
    key: 'pod_duration',
    label: 'Pod duration',
    requested: '—',
    delivered: `${deliveredDur}s`,
    state: 'info',
    note: 'no duration budget to compare',
  };
}

function buildPodBreaksCheck(vmapRequested, vast) {
  const hasBreaks = !!(vast && vast.vmap && vast.vmap.breakCount > 0);

  if (vmapRequested && !hasBreaks) {
    return {
      key: 'pod_breaks',
      label: 'Ad breaks',
      requested: 'VMAP ad-rules',
      delivered: 'no ad breaks',
      state: 'warn',
      note: 'VMAP requested but the response carried no ad breaks (empty pod)',
    };
  }

  if (hasBreaks) {
    const { breakCount, prerolls, midrolls, postrolls } = vast.vmap;
    return {
      key: 'pod_breaks',
      label: 'Ad breaks',
      requested: '—',
      delivered: `${breakCount} breaks (${prerolls} pre / ${midrolls} mid / ${postrolls} post)`,
      state: 'info',
      note: '',
    };
  }

  return null;
}

function buildCompSepCheck(ads) {
  if (!Array.isArray(ads) || ads.length <= 1) return null;

  const anyAdvertiser = ads.some(
    (ad) => ad && typeof ad.advertiser === 'string' && ad.advertiser.trim() !== ''
  );
  if (!anyAdvertiser) return null;

  for (let i = 1; i < ads.length; i++) {
    const prev = ads[i - 1];
    const cur = ads[i];
    const prevAdv = prev && typeof prev.advertiser === 'string' ? prev.advertiser.trim() : '';
    const curAdv = cur && typeof cur.advertiser === 'string' ? cur.advertiser.trim() : '';
    if (prevAdv && curAdv && prevAdv.toLowerCase() === curAdv.toLowerCase()) {
      return {
        key: 'pod_compsep',
        label: 'Competitive separation',
        requested: 'distinct advertisers',
        delivered: `${curAdv} back-to-back`,
        state: 'warn',
        note: 'same advertiser in consecutive pod slots — competitive-separation break',
      };
    }
  }

  return {
    key: 'pod_compsep',
    label: 'Competitive separation',
    requested: 'distinct advertisers',
    delivered: 'no adjacent repeats',
    state: 'pass',
    note: '',
  };
}

export function videoPodRealization(row) {
  const type = row?.requestType || row?.type;
  if (!VIDEO_TYPES.has(type)) return null;

  const vast = row?.validData?.vastResponse || row?.validData?.vast || null;

  const params = parseQueryParams(row?.url);
  const ad_rule = params ? params.get('ad_rule') : null;
  const output = params ? params.get('output') : null;
  const pmad = params ? params.get('pmad') : null;
  const pmxd = params ? params.get('pmxd') : null;

  const imp = row?.validData?.videoOrtb?.imps?.[0] || null;

  const reqPodGam = ad_rule === '1' || /vmap/i.test(output || '') || pmad != null || pmxd != null;
  const reqPodOrtb = !!(imp && (imp.maxseq != null || imp.poddur != null || imp.podid != null));
  const reqPod = reqPodGam || reqPodOrtb;

  const delivPod = !!(vast && vast.vmap && vast.vmap.breakCount > 0);

  if (!reqPod && !delivPod) return null;

  const checks = [];

  // Pod fill: requested max ads vs delivered ad count.
  const maxAdsRaw = pmad != null ? pmad : (imp && imp.maxseq != null ? imp.maxseq : null);
  const maxAds = toNumberOrNull(maxAdsRaw);
  const deliveredAds = vast && typeof vast.adCount === 'number' ? vast.adCount : null;
  const podFillCheck = buildPodFillCheck(maxAds, deliveredAds);
  if (podFillCheck) checks.push(podFillCheck);

  // Pod duration: requested max duration vs summed delivered ad durations.
  const podMaxRaw = pmxd != null ? pmxd : (imp && imp.poddur != null ? imp.poddur : null);
  const podMax = toNumberOrNull(podMaxRaw);
  const deliveredDur = sumDeliveredDuration(vast && vast.ads);
  const podDurationCheck = buildPodDurationCheck(podMax, deliveredDur);
  if (podDurationCheck) checks.push(podDurationCheck);

  // Break structure: was VMAP requested, and did any ad breaks actually deliver?
  const vmapRequested = ad_rule === '1' || /vmap/i.test(output || '');
  const podBreaksCheck = buildPodBreaksCheck(vmapRequested, vast);
  if (podBreaksCheck) checks.push(podBreaksCheck);

  // Competitive separation: same advertiser in adjacent pod slots.
  const compSepCheck = buildCompSepCheck(vast && vast.ads);
  if (compSepCheck) checks.push(compSepCheck);

  let level = 'pass';
  let flagged = 0;
  for (const c of checks) {
    if (c.state === 'warn' || c.state === 'fail') flagged++;
    if (c.state === 'fail') {
      level = 'fail';
    } else if (c.state === 'warn' && level !== 'fail') {
      level = 'warn';
    }
  }

  return {
    isPod: true,
    checks,
    verdict: { level, flagged },
  };
}
